/**
 * Pure Lexical logic to generate transcript editor state from word data.
 * Extracted into its own file so it can be imported by the headless transcript-service
 * without pulling in React, Redux, Luminary, or other browser dependencies.
 */
import { $createLineBreakNode, $createParagraphNode, $getRoot } from 'lexical';

import {
    $createSilenceNode,
    $isSilenceNode,
    SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT,
} from 'js/src/data/transcript/nodes/SilenceNode';
import {
    $createTranscriptWordNode,
    $isTranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';

// Inlined from js/src/libs/consts.ts TRANSCRIPT_CONFIG to keep this file free of
// transitive dependencies (consts.ts imports @lumen5/luminary, Redux, etc.)
const LINEBREAK_PAUSE_THRESHOLD_MS = 0.4 * 1000; // LINEBREAK_PAUSE_THRESHOLD_MS
const MAX_PARAGRAPH_SENTENCE_COUNT = 3; // MAX_PARAGRAPH_SENTENCE_COUNT
const SILENCE_NODE_THRESHOLD_MILLIS = 1001; // SILENCE_NODE_THRESHOLD_MILLIS
const SILENCE_NODE_TARGET_DURATION_MILLIS = 1000; // SILENCE_NODE_TARGET_DURATION_MILLIS

function uuid(): string {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export interface TranscriptWordInput {
    word: string;
    startTime: number;
    endTime: number;
    filler: boolean;
    id?: string;
}

export enum SilenceNodeGenerationMethod {
    DoNotGenerate = 'DoNotGenerate',
    GenerateFromTranscriptWord = 'GenerateFromTranscriptWord',
    GenerateFromWordGap = 'GenerateFromWordGap',
}

interface SilenceNodeData {
    startTime: number;
    endTime: number;
}

function getSilenceNodeTimes(startTimeMillis: number, endTimeMillis: number): SilenceNodeData[] {
    const pauseMillis = endTimeMillis - startTimeMillis;
    if (pauseMillis < SILENCE_NODE_THRESHOLD_MILLIS) {
        return [];
    }
    const numSilenceNodes = Math.round(pauseMillis / SILENCE_NODE_TARGET_DURATION_MILLIS);
    if (!numSilenceNodes) {
        return [];
    }
    const actualSilenceDuration = pauseMillis / numSilenceNodes;
    let silenceStartTime = startTimeMillis;
    const silences: SilenceNodeData[] = [];
    for (let i = 0; i < numSilenceNodes; i++) {
        const silenceEndTime = Math.min(silenceStartTime + actualSilenceDuration, endTimeMillis);
        silences.push({ startTime: silenceStartTime, endTime: silenceEndTime });
        silenceStartTime = silenceEndTime;
    }
    return silences;
}

function $isSpace(node: ReturnType<typeof $createTranscriptWordNode> | null | undefined): boolean {
    return $isTranscriptWordNode(node) && node.isEmptyText();
}

/**
 * Generates Lexical word nodes, line breaks, and silence nodes from transcript words.
 *
 * @param durationMillis : Only applicable when silenceNodeGenerationMethod is GenerateFromWordGap
 */
export const $generateEditorFromTranscript = (
    transcriptWords: TranscriptWordInput[],
    langUsesSpaces: boolean,
    silenceNodeGenerationMethod: SilenceNodeGenerationMethod,
    durationMillis?: number,
) => {
    const root = $getRoot();
    root.clear();
    const paragraphNode = $createParagraphNode();
    let prevWordEndTime = 0;
    const createSilenceNode = (startTime: number, endTime: number, id?: string) => {
        const silenceNode = $createSilenceNode(startTime, endTime, { id });
        paragraphNode.append(silenceNode);
    };

    transcriptWords.forEach((entry: TranscriptWordInput) => {
        const wordStartMillis = entry.startTime;
        const wordEndMillis = entry.endTime;

        if (silenceNodeGenerationMethod === SilenceNodeGenerationMethod.GenerateFromWordGap) {
            const silenceNodeTimes = getSilenceNodeTimes(prevWordEndTime, wordStartMillis);
            silenceNodeTimes.forEach((entry) => {
                const { startTime, endTime } = entry;
                createSilenceNode(startTime, endTime);
            });
        }

        // Create node from the transcript word
        const id = entry.id ?? uuid();
        if (
            silenceNodeGenerationMethod ===
                SilenceNodeGenerationMethod.GenerateFromTranscriptWord &&
            entry.word === SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT
        ) {
            createSilenceNode(entry.startTime, entry.endTime, id);
        } else {
            const textNode = $createTranscriptWordNode({
                text: entry.word,
                startTime: entry.startTime,
                endTime: entry.endTime,
                isFiller: entry.filler,
                id,
            });
            paragraphNode.append(textNode);
            if (langUsesSpaces) {
                const spaceNode = $createTranscriptWordNode({
                    text: ' ',
                    startTime: entry.endTime,
                    endTime: entry.endTime,
                    isFiller: false,
                });
                paragraphNode.append(spaceNode);
            }
        }

        prevWordEndTime = wordEndMillis;
    });
    if (
        silenceNodeGenerationMethod === SilenceNodeGenerationMethod.GenerateFromWordGap &&
        durationMillis
    ) {
        const silenceNodeTimes = getSilenceNodeTimes(prevWordEndTime, durationMillis);
        silenceNodeTimes.forEach((entry) => {
            const { startTime, endTime } = entry;
            createSilenceNode(startTime, endTime);
        });
    }

    // Create line breaks nodes
    const wordNodes = paragraphNode.getChildren().filter($isTranscriptWordNode);
    if (wordNodes.length !== paragraphNode.getChildrenSize()) {
        throw new Error('All paragraph node children should be transcript word nodes');
    }
    let currentSentenceCount = 0;
    let currentSilenceCount = 0;
    let index = 0;
    let prevWordLastChar = '';
    let prevTextWordEndTime = 0;
    while (index < wordNodes.length) {
        let node = wordNodes[index];
        if ($isSpace(node)) {
            index++;
            continue;
        }
        let nextWordIndex = index;
        while ($isSilenceNode(node) && nextWordIndex < wordNodes.length) {
            nextWordIndex++;
            node = wordNodes[nextWordIndex];
            currentSilenceCount++;
        }
        if (!node) {
            break;
        }
        const wordStartMillis = node.startTime;
        const wordGapMillis = wordStartMillis - prevTextWordEndTime;
        const wordGapOverThreshold = wordGapMillis >= LINEBREAK_PAUSE_THRESHOLD_MS;
        const shouldCreateLineBreak =
            prevTextWordEndTime &&
            (wordGapOverThreshold || currentSentenceCount >= MAX_PARAGRAPH_SENTENCE_COUNT) &&
            ['.', '?'].includes(prevWordLastChar);

        if (shouldCreateLineBreak) {
            const indexToInsert = Math.floor(currentSilenceCount / 2) + index - 1;
            wordNodes[indexToInsert].insertAfter($createLineBreakNode());
            currentSentenceCount = 0;
        }

        const prevWordText = node.getTextContent();
        prevWordLastChar = prevWordText[prevWordText.length - 1];
        prevTextWordEndTime = node.endTime;
        if (['.', '?'].includes(prevWordLastChar)) {
            currentSentenceCount += 1;
        }
        currentSilenceCount = 0;
        index = nextWordIndex + 1;
    }

    root.append(paragraphNode);
};
